import { useState, useMemo } from "react";
import { Shield, Globe, MoreHorizontal, Info, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNetworkRulesWithScope, type NetworkRuleWithScope } from "@/hooks/useNetworkRulesWithScope";
import { useNetworkRuleDetail } from "@/hooks/useNetworkRuleDetail";
import { NetworkRuleDetail } from "./NetworkRuleDetail";
import { AppIdDisplay } from "./AppIdDisplay";
import { useDeviceRules } from "@/hooks/useDeviceRules";

interface DeviceNetworkRulesTableProps {
  deviceId: string;
  ruleType?: "system-wide" | "app-specific";
  deviceRules?: any[];
}

interface GroupedAppRule {
  parentApp: {
    id: string;
    name: string;
    iconUrl?: string;
    bundleId: string;
  };
  rules: NetworkRuleWithScope[];
}

export const DeviceNetworkRulesTable = ({ deviceId, ruleType, deviceRules: propDeviceRules = [] }: DeviceNetworkRulesTableProps) => {
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  
  const { data: allNetworkRules = [], isLoading } = useNetworkRulesWithScope();
  const { data: selectedRuleDetail, isLoading: isLoadingDetail } = useNetworkRuleDetail(selectedRuleId);
  
  // Fetch device-specific rules to know which rules apply to this device
  const deviceRulesData = useDeviceRules(deviceId);

  // Filter rules to only show those applied to this device
  const filteredRules = useMemo(() => {
    if (!allNetworkRules || !deviceRulesData) return [];
    
    // Get bundle IDs of rules that are actually applied to this device
    const appliedRuleBundleIds = new Set(
      deviceRulesData
        .filter(rule => rule.source !== false) // Only rules applied to device (source is "device" or "level")
        .map(rule => rule.bundleId)
    );
    
    return allNetworkRules.filter(rule => {
      // Filter by rule type (system-wide vs app-specific)
      let typeMatch = true;
      if (ruleType === "system-wide") {
        typeMatch = rule.category === "system-wide";
      } else if (ruleType === "app-specific") {
        typeMatch = rule.category === "app-specific";
      }
      
      // Only include rules that are applied to this device
      const isApplied = appliedRuleBundleIds.has(rule.bundleId);
      
      return typeMatch && isApplied;
    });
  }, [allNetworkRules, deviceRulesData, ruleType]);

  const groupedByParentApp = (rules: NetworkRuleWithScope[]): GroupedAppRule[] => {
    const groupsMap = new Map<string, GroupedAppRule>();

    rules.forEach(rule => {
      if (rule.parentApp) {
        const key = rule.parentApp.id;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            parentApp: rule.parentApp,
            rules: []
          });
        }
        groupsMap.get(key)!.rules.push(rule);
      }
    });

    return Array.from(groupsMap.values());
  };

  const handleRuleClick = (rule: NetworkRuleWithScope, event: React.MouseEvent) => {
    setSelectedRuleId(rule.id);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedRuleId(null);
  };

  const handleAppToggle = (appId: string) => {
    const newExpanded = new Set(expandedApps);
    if (newExpanded.has(appId)) {
      newExpanded.delete(appId);
    } else {
      newExpanded.add(appId);
    }
    setExpandedApps(newExpanded);
  };

  if (isLoading || !deviceRulesData) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (filteredRules.length === 0) {
    const IconComponent = ruleType === "system-wide" ? Globe : Shield;
    const emptyMessage = ruleType === "system-wide" 
      ? "No system-wide network rules are currently assigned to this device."
      : "No app-specific network rules are currently assigned to this device.";
    
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <IconComponent className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {ruleType === "system-wide" ? "System-Wide" : "App-Specific"} Rules
          </h3>
          <p className="text-gray-500 text-center">
            {emptyMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  // For app-specific rules, group by parent app
  if (ruleType === "app-specific") {
    const parentAppGroups = groupedByParentApp(filteredRules);

    return (
      <>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[400px]">App / Rule</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parentAppGroups.map((group) => (
                  <>
                    {/* Parent App Header Row */}
                    <TableRow 
                      key={`app-${group.parentApp.id}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors bg-gray-25 h-12"
                      onClick={() => handleAppToggle(group.parentApp.id)}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center">
                          {expandedApps.has(group.parentApp.id) ? (
                            <ChevronDown className="w-4 h-4 mr-2 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-2 text-gray-500" />
                          )}
                          <AppIdDisplay appId={group.parentApp.id} />
                        </div>
                       </TableCell>
                       <TableCell className="py-2">
                        <button 
                          className="text-gray-400 hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log("More options clicked for app");
                          }}
                        >
                          <MoreHorizontal className="w-5 h-5" />
                        </button>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Rules */}
                    {expandedApps.has(group.parentApp.id) && group.rules.map((rule, index) => {
                      // Log warning if rule.id is missing
                      if (!rule.id) {
                        console.warn('Rule missing ID:', { rule, parentApp: group.parentApp.id, index });
                      }
                      return (
                        <TableRow 
                          key={rule.id || `rule-${group.parentApp.id}-${index}`}
                          className="hover:bg-gray-50 cursor-pointer transition-colors h-10"
                          onClick={(e) => handleRuleClick(rule, e)}
                        >
                          <TableCell className="py-1">
                            <div className="flex items-center pl-8">
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="font-medium text-gray-900 text-sm">{rule.name}</div>
                                </div>
                                {rule.description && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-xs">{rule.description}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                           </TableCell>
                           <TableCell className="py-1">
                            <button 
                              className="text-gray-400 hover:text-gray-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log("More options clicked for rule");
                              }}
                            >
                              <MoreHorizontal className="w-5 h-5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                ))}
                
                {parentAppGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center py-8 text-gray-500">
                      No app-specific rules available for this device
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Rule Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-6xl h-[80vh] p-0">
            {isLoadingDetail ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : selectedRuleDetail ? (
              <NetworkRuleDetail 
                rule={selectedRuleDetail as any} 
                onBack={handleCloseDialog}
                onDelete={() => {
                  handleCloseDialog();
                }}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // For system-wide rules, show the flat table
  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[400px]">Rule Name</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRules.map((rule, index) => {
                return (
                  <TableRow 
                    key={rule.id || `system-rule-${index}`}
                    className="hover:bg-gray-50 cursor-pointer transition-colors h-10"
                    onClick={(e) => handleRuleClick(rule, e)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{rule.name}</div>
                          </div>
                          {rule.description && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{rule.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                     </TableCell>
                     <TableCell className="py-2">
                      <button 
                        className="text-gray-400 hover:text-gray-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("More options clicked for rule");
                        }}
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Rule Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl h-[80vh] p-0">
          {isLoadingDetail ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : selectedRuleDetail ? (
            <NetworkRuleDetail 
              rule={selectedRuleDetail as any} 
              onBack={handleCloseDialog}
              onDelete={() => {
                handleCloseDialog();
              }}
            />
          ) : null}
        </DialogContent>
        </Dialog>
      </>
  );
};